import { type FetchResult, useMutation } from '@apollo/client';
import { SET_TTD_OTS_BUYABLE_SEGMENTS } from '../queries';

export type SetTtdOtsBuyableSegmentsSummary = {
  requested: number;
  succeeded: number;
  failed: number;
};

export type SetTtdOtsBuyableSegmentsError = {
  code: string | null;
  message: string | null;
  retriable: boolean | null;
};

export type SetTtdOtsBuyableSegmentsResultRow = {
  segmentId: string;
  status: string;
  error?: SetTtdOtsBuyableSegmentsError | null;
};

export type SetTtdOtsBuyableSegmentsPayload = {
  summary: SetTtdOtsBuyableSegmentsSummary;
  results: SetTtdOtsBuyableSegmentsResultRow[];
  errors: string[];
};

type SetTtdOtsBuyableSegmentsMutationData = {
  setTtdOtsBuyableSegments: SetTtdOtsBuyableSegmentsPayload;
};

/**
 * Mutation to set TTD OTS segment buyable state (bulk) via taxonomy-service.
 * @returns [setTtdOtsBuyableSegments, { loading, error, data }]
 */
export function useSetTtdOtsBuyableSegments(): [
  (vars: {
    segmentIds: string[];
    buyable?: boolean;
  }) => Promise<FetchResult<SetTtdOtsBuyableSegmentsMutationData>>,
  {
    loading: boolean;
    error: Error | undefined;
    data: SetTtdOtsBuyableSegmentsPayload | undefined;
  }
] {
  const [mutate, { loading, error, data }] =
    useMutation<SetTtdOtsBuyableSegmentsMutationData>(
      SET_TTD_OTS_BUYABLE_SEGMENTS
    );

  const setTtdOtsBuyableSegments = async (variables: {
    segmentIds: string[];
    buyable?: boolean;
  }) => mutate({ variables });

  return [
    setTtdOtsBuyableSegments,
    {
      loading,
      error: error ?? undefined,
      data: data?.setTtdOtsBuyableSegments,
    },
  ];
}
